import { useState } from 'react';

interface Question {
  id: number;
  question: string;
  options?: string[];
  correctAnswer: string;
  solution: string;
}

interface Example {
  problem: string;
  solution: string;
}

interface Lesson {
  id: number;
  title: string;
  description: string;
  vocabulary: string[];
  conceptExplanation: string;
  examples: Example[];
  teacherTip: string;
  questions: Question[];
}

interface LessonCardProps {
  lesson: Lesson;
}

export default function LessonCard({ lesson }: LessonCardProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'practice'>('overview');
  const [userAnswers, setUserAnswers] = useState<Record<number, string>>({});
  const [showSolutions, setShowSolutions] = useState<Record<number, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [filter, setFilter] = useState<'all' | 'correct' | 'incorrect'>('all');

  const handleAnswerChange = (questionId: number, answer: string) => {
    setUserAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  const handleReset = () => {
    setUserAnswers({});
    setShowSolutions({});
    setSubmitted(false);
    setFilter('all');
  };

  const toggleSolution = (questionId: number) => {
    setShowSolutions(prev => ({ ...prev, [questionId]: !prev[questionId] }));
  };

  const showAllSolutions = () => {
    const allSolutions: Record<number, boolean> = {};
    lesson.questions.forEach(q => {
      allSolutions[q.id] = true;
    });
    setShowSolutions(allSolutions);
  };

  const score = lesson.questions.reduce((acc, q) => {
    return acc + (userAnswers[q.id] === q.correctAnswer ? 1 : 0);
  }, 0);

  const filteredQuestions = lesson.questions.filter(q => {
    if (filter === 'all') return true;
    if (filter === 'correct') return userAnswers[q.id] === q.correctAnswer;
    if (filter === 'incorrect') return userAnswers[q.id] && userAnswers[q.id] !== q.correctAnswer;
    return true;
  });

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-indigo-600 to-violet-600 p-6 text-white">
        <h3 className="text-2xl font-bold mb-2">{lesson.title}</h3>
        <p className="text-indigo-100">{lesson.description}</p>
      </div>

      <div className="border-b border-gray-200">
        <div className="flex">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'overview'
                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-50'
            }`}
          >
            <i className="ri-book-open-line mr-2"></i>
            Lesson Overview
          </button>
          <button
            onClick={() => setActiveTab('practice')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors whitespace-nowrap ${
              activeTab === 'practice'
                ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50'
                : 'text-gray-600 hover:text-indigo-600 hover:bg-gray-50'
            }`}
          >
            <i className="ri-pencil-line mr-2"></i>
            Practice Questions (30)
          </button>
        </div>
      </div>

      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-bookmark-line text-indigo-600 mr-2"></i>
                Key Vocabulary
              </h4>
              <div className="flex flex-wrap gap-2">
                {lesson.vocabulary.map((term, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium"
                  >
                    {term}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                <i className="ri-lightbulb-line text-indigo-600 mr-2"></i>
                Concept Explanation
              </h4>
              <div className="prose prose-indigo max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{lesson.conceptExplanation}</p>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-file-list-3-line text-indigo-600 mr-2"></i>
                Worked Examples
              </h4>
              <div className="space-y-6">
                {lesson.examples.map((example, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <div className="flex items-start mb-3">
                      <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">
                        {idx + 1}
                      </span>
                      <p className="text-gray-900 font-semibold flex-1">{example.problem}</p>
                    </div>
                    <div className="ml-11 text-gray-700 whitespace-pre-line leading-relaxed">
                      {example.solution}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-5 border border-amber-200">
              <div className="flex items-start">
                <i className="ri-user-smile-line text-amber-600 text-2xl mr-3 flex-shrink-0"></i>
                <div>
                  <h4 className="text-lg font-bold text-amber-900 mb-2">Mr. Augustine's Teaching Tip</h4>
                  <p className="text-amber-800 leading-relaxed">{lesson.teacherTip}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'practice' && (
          <div>
            {!submitted && (
              <div className="mb-6 bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                <p className="text-indigo-900 font-medium">
                  <i className="ri-information-line mr-2"></i>
                  Answer all 30 questions, then click Submit to see your score and solutions.
                </p>
              </div>
            )}

            {submitted && (
              <div className="mb-6 space-y-4">
                <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-2xl font-bold mb-1">
                        Your Score: {score} / {lesson.questions.length}
                      </h4>
                      <p className="text-indigo-100">
                        {Math.round((score / lesson.questions.length) * 100)}% Correct
                      </p>
                    </div>
                    <div className="text-5xl font-bold opacity-90">
                      {Math.round((score / lesson.questions.length) * 100)}%
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <div className="flex gap-2 border-r border-gray-300 pr-3">
                    <button
                      onClick={() => setFilter('all')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                        filter === 'all'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      All Questions
                    </button>
                    <button
                      onClick={() => setFilter('correct')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                        filter === 'correct'
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <i className="ri-check-line mr-1"></i>
                      Correct ({score})
                    </button>
                    <button
                      onClick={() => setFilter('incorrect')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                        filter === 'incorrect'
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <i className="ri-close-line mr-1"></i>
                      Incorrect ({lesson.questions.length - score})
                    </button>
                  </div>
                  <button
                    onClick={showAllSolutions}
                    className="px-4 py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-eye-line mr-2"></i>
                    Show All Solutions
                  </button>
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors whitespace-nowrap"
                  >
                    <i className="ri-refresh-line mr-2"></i>
                    Retake
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {filteredQuestions.map((q, idx) => {
                const isCorrect = userAnswers[q.id] === q.correctAnswer;
                const isAnswered = userAnswers[q.id] !== undefined;

                return (
                  <div
                    key={q.id}
                    className={`rounded-lg border-2 p-5 transition-all ${
                      submitted && isAnswered
                        ? isCorrect
                          ? 'border-green-300 bg-green-50'
                          : 'border-red-300 bg-red-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start mb-4">
                      <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm mr-3">
                        {q.id}
                      </span>
                      <p className="text-gray-900 font-medium flex-1">{q.question}</p>
                      {submitted && isAnswered && (
                        <span
                          className={`ml-3 px-3 py-1 rounded-full text-sm font-bold whitespace-nowrap ${
                            isCorrect ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                          }`}
                        >
                          {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                        </span>
                      )}
                    </div>

                    {q.options ? (
                      <div className="ml-11 space-y-2">
                        {q.options.map((option, optIdx) => (
                          <label
                            key={optIdx}
                            className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              userAnswers[q.id] === option
                                ? submitted
                                  ? option === q.correctAnswer
                                    ? 'border-green-500 bg-green-100'
                                    : 'border-red-500 bg-red-100'
                                  : 'border-indigo-500 bg-indigo-50'
                                : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`question-${q.id}`}
                              value={option}
                              checked={userAnswers[q.id] === option}
                              onChange={() => handleAnswerChange(q.id, option)}
                              disabled={submitted}
                              className="mr-3 w-4 h-4 text-indigo-600 cursor-pointer"
                            />
                            <span className="text-gray-900">{option}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="ml-11">
                        <input
                          type="text"
                          value={userAnswers[q.id] || ''}
                          onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                          disabled={submitted}
                          placeholder="Type your answer here"
                          className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                        />
                      </div>
                    )}

                    {submitted && (
                      <div className="ml-11 mt-4">
                        <button
                          onClick={() => toggleSolution(q.id)}
                          className="text-indigo-600 hover:text-indigo-800 font-semibold flex items-center transition-colors"
                        >
                          <i className={`${showSolutions[q.id] ? 'ri-eye-off-line' : 'ri-eye-line'} mr-2`}></i>
                          {showSolutions[q.id] ? 'Hide' : 'Show'} Solution
                        </button>
                        {showSolutions[q.id] && (
                          <div className="mt-3 p-4 bg-white rounded-lg border border-gray-300">
                            <p className="text-sm font-bold text-gray-900 mb-2">
                              Correct Answer: {q.correctAnswer}
                            </p>
                            <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                              {q.solution}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {!submitted && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={handleSubmit}
                  disabled={Object.keys(userAnswers).length !== lesson.questions.length}
                  className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-lg font-bold text-lg hover:from-indigo-700 hover:to-violet-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <i className="ri-check-double-line mr-2"></i>
                  Submit Answers
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}